import * as React from "react"
import { Box } from "@indeed/ifl-components"
import { configSelector } from "../../../common/utils/config-slice"
import { useI18n } from "../../../common/utils/i18n-context"
import { jobInfoSelector } from "../../../common/utils/job-info"
import { useAppDispatch, useAppSelector } from "../../../common/utils/redux-helpers"
import { isMultiSelectDisplayItem } from "../feedback-form-model"
import { actions, optsSelector, toggleAdditionalLocationSelectionAction } from "../feedback-form-slice"
import { PreferenceItem, getPreferenceCategoryNamesMap } from "../preference-item"
import { MultiselectPreferenceItem } from "./multiselect-preference-item"

export interface DislikePreferencesSelectorProps {
    ariaLabelledBy?: string
}

export const DislikePreferencesSelector: React.FC<DislikePreferencesSelectorProps> = (props) => {
    const { ariaLabelledBy } = props
    const dispatch = useAppDispatch()
    const displayItems = useAppSelector(optsSelector)
    const jobInfo = useAppSelector(jobInfoSelector)
    const config = useAppSelector(configSelector)
    const showNewContentStrings = config.proctorGroups.jsj_mrp_jcr_content_strings_tst === 1
    const { gettext } = useI18n()

    const preferenceCategoryNamesMap = getPreferenceCategoryNamesMap(gettext)

    return (
        <Box
            as="ul"
            sx={{
                listStyle: "none",
                paddingBlockEnd: 0,
                paddingBlockStart: 0,
                paddingInlineEnd: 0,
                paddingInlineStart: 0,
                inlineSize: "100%",
            }}
            id="dislike-preferences-options"
            aria-labelledby={ariaLabelledBy}
        >
            {displayItems.map((item, i) => {
                const oldContentStringsButtonAriaLabel = item.selected
                    ? gettext("Undo not interested in {0}", `${preferenceCategoryNamesMap[item.iconCategory]}: ${item.unselectedText}`)
                    : gettext("Not interested in {0}", `${preferenceCategoryNamesMap[item.iconCategory]}: ${item.unselectedText}`)
                const newContentStringsButtonAriaLabel = item.selected
                    ? gettext("Unhide {0}", `${preferenceCategoryNamesMap[item.iconCategory]}: ${item.unselectedText}`)
                    : gettext("Hide {0}", `${preferenceCategoryNamesMap[item.iconCategory]}: ${item.unselectedText}`)
                const buttonAriaLabel = showNewContentStrings ? newContentStringsButtonAriaLabel : oldContentStringsButtonAriaLabel

                if (isMultiSelectDisplayItem(item)) {
                    return (
                        <MultiselectPreferenceItem
                            {...{
                                ...item,
                                buttonAriaLabel,
                                category: item.iconCategory,
                                bodyText: item.selected ? item.selectedText : item.unselectedText,
                                toggleSelect: () => {
                                    dispatch(actions.setSelectedLocationText({ job: jobInfo, gettext }))
                                    dispatch(actions.toggleSelected(i))
                                },
                                additionalSelectionSelectedHandler: (_, additionalSelectionIdx) => {
                                    dispatch(
                                        toggleAdditionalLocationSelectionAction({
                                            additionalSelectionIdx,
                                            optionIdx: i,
                                            gettext,
                                        })
                                    )
                                },
                            }}
                        />
                    )
                }

                return (
                    <PreferenceItem
                        id={`dislike-preferences-selector-item-${item.field}-${item.value}`}
                        buttonAriaLabel={buttonAriaLabel}
                        key={item.key}
                        selected={item.selected}
                        toggleSelect={() => {
                            dispatch(actions.toggleSelected(i))
                        }}
                        bodyText={item.selected ? item.selectedText : item.unselectedText}
                        category={item.iconCategory}
                    />
                )
            })}
        </Box>
    )
}
